/**
 * Core validation logic for polygon dimensions.
 * Verifies that a polygon has at least 3 sides/angles (minimum for a valid polygon).
 * Para un poligono chiuso: path.length >= 4 (3 vertici unici + 1 punto di chiusura)
 * @param {Array} path - Array di oggetti con lat/lng
 * @returns {Object} { isValid: boolean, count: number }
 */
const validatePolygonDimensions = (path) => {
  if (!path || path.length < 3) {
    return {
      isValid: false,
      count: path?.length || 0,
    };
  }

  const count = path.length - 1;
  const isValid = count >= 3;

  return {
    isValid,
    count,
  };
};

/**
 * Verifica se il numero di lati del poligono è >= 3 (minimum per un poligono valido)
 * Per un poligono chiuso: path.length >= 4 (3 vertici unici + 1 punto di chiusura)
 * Quindi: sidesCount = path.length - 1 >= 3
 * @param {Array} path - Array di oggetti con lat/lng
 * @returns {Object} { isValid: boolean, sidesCount: number, message: string }
 */
export const validateSidesLength = (path) => {
  const { isValid, count } = validatePolygonDimensions(path);

  return {
    isValid,
    sidesCount: count,
    message: isValid 
      ? `Numero lati valido: ${count} lati (≥ 3) ✓`
      : `Numero lati non valido: ${count} lati (< 3) ✗`,
  };
};

/**
 * Verifica se il numero di angoli del poligono è >= 3 (minimum per un poligono valido)
 * Per un poligono chiuso: path.length >= 4 (3 vertici unici + 1 punto di chiusura)
 * Quindi: anglesCount = path.length - 1 >= 3
 * @param {Array} path - Array di oggetti con lat/lng
 * @returns {Object} { isValid: boolean, anglesCount: number, message: string }
 */
export const validateAngles = (path) => {
  const { isValid, count } = validatePolygonDimensions(path);

  return {
    isValid,
    anglesCount: count,
    message: isValid
      ? `Numero angoli valido: ${count} angoli (≥ 3) ✓`
      : `Numero angoli non valido: ${count} angoli (< 3) ✗`,
  };
};

/**
 * Verifica sia il numero di lati che il numero di angoli in una sola chiamata.
 * Un poligono valido deve avere almeno 3 lati e 3 angoli (triangolo).
 * Constraint: path.length >= 4 (3 vertici unici + 1 punto di chiusura per chiusura del poligono)
 * Se lati >= 3 E angoli >= 3, è un poligono chiuso e tracciabile.
 * @param {Array} path - Array di oggetti con lat/lng
 * @returns {Object} { sidesValid: boolean, anglesValid: boolean, isValid: boolean, details: Object }
 */
export const validatePolygonGeometry = (path) => {
  const { isValid, count } = validatePolygonDimensions(path);

  return {
    isValid,
    sidesValid: isValid,
    anglesValid: isValid,
    details: {
      sides: {
        isValid,
        sidesCount: count,
        message: isValid 
          ? `Numero lati valido: ${count} lati (≥ 3) ✓`
          : `Numero lati non valido: ${count} lati (< 3) ✗`,
      },
      angles: {
        isValid,
        anglesCount: count,
        message: isValid
          ? `Numero angoli valido: ${count} angoli (≥ 3) ✓`
          : `Numero angoli non valido: ${count} angoli (< 3) ✗`,
      },
    },
    summary: isValid
      ? 'Poligono geometricamente valido ✓'
      : 'Poligono con problemi geometrici ✗',
  };
};
